import React, { useState } from 'react'
import { GdprSectionProps } from './GdprSection.types'
import styles from './GdprSection.module.scss'
import { Button, H2, SectionContainer } from 'components/shared'
import { Api, User } from 'utilities'
import { inject } from 'mobx-react'
import { GENERAL_STORE, USER_PROFILE_STORE } from 'store'
import { observer } from 'mobx-react-lite'

const GdprSectionComponent = (props: GdprSectionProps) => {
  const {
    [USER_PROFILE_STORE]: userProfileStore,
    [GENERAL_STORE]: generalStore
  } = props

  const [exportLoading, setExportLoading] = useState(false)
  const [erasureLoading, setErasureLoading] = useState(false)
  const [lastExportResult, setLastExportResult] = useState<Record<string, unknown> | null>(null)
  const [showExportResult, setShowExportResult] = useState(false)

  const user = userProfileStore?.user
  const email = user?.emailAddress?.trim()
  const isAdmin = User.isAdmin()

  if (!isAdmin || !email) return null

  const handleExport = async () => {
    if (!generalStore || !email) return
    setExportLoading(true)
    setLastExportResult(null)
    generalStore.setLoadingOn()
    try {
      const result = await Api.post<Record<string, unknown>>(
        '/gdpr/export',
        { email },
        'mainApi'
      )
      generalStore.setLoadingOff()
      setExportLoading(false)
      setLastExportResult(result ?? null)
      setShowExportResult(true)
      generalStore.showSuccess('User data export completed. View the result below.')
      if (result) {
        console.info('GDPR export result:', result)
      }
    } catch (err) {
      generalStore.setLoadingOff()
      setExportLoading(false)
      generalStore.handleApiError(err as Error)
    }
  }

  const handleErasureClick = () => {
    if (!generalStore || !email) return
    generalStore.confirmAction({
      title: 'Erase user data (GDPR)',
      description: 'This will permanently delete or anonymize this user\'s data across all services (right to erasure). This action cannot be undone. Are you sure?',
      submitText: 'Erase',
      onSubmit: async () => {
        setErasureLoading(true)
        generalStore.setLoadingOn()
        try {
          await Api.post<Record<string, unknown>>(
            '/gdpr/erasure',
            { email, anonymizeOrders: true },
            'mainApi'
          )
          generalStore.setLoadingOff()
          setErasureLoading(false)
          generalStore.hideModalDialog()
          generalStore.showSuccess('User data erasure completed.')
          userProfileStore?.clearStore()
          generalStore.push('/user-management')
        } catch (err) {
          generalStore.setLoadingOff()
          setErasureLoading(false)
          generalStore.handleApiError(err as Error)
        }
      }
    })
  }

  return (
    <SectionContainer style={{ marginTop: 20 }} className={styles.section}>
      <H2>GDPR (Super Admin)</H2>
      <p style={{ marginTop: 8, marginBottom: 0, opacity: 0.85 }}>
        Export or erase this user&apos;s personal data across all services. Requires SUPER_ADMIN role.
      </p>
      <div className={styles.buttonsRow}>
        <Button
          onClick={handleExport}
          disabled={exportLoading}
          preset="secondary"
        >
          {exportLoading ? 'Exporting…' : 'Export user data'}
        </Button>
        {/* <Button
          onClick={handleErasureClick}
          disabled={erasureLoading}
          preset="primary"
        >
          {erasureLoading ? 'Erasing…' : 'Erase user data'}
        </Button> */}
      </div>
      {lastExportResult != null && (
        <div className={styles.exportResult}>
          <button
            type="button"
            className={styles.exportResultToggle}
            onClick={() => setShowExportResult((v) => !v)}
          >
            {showExportResult ? 'Hide export result' : 'Show export result'}
          </button>
          {showExportResult && (
            <pre className={styles.exportResultJson}>
              {JSON.stringify(lastExportResult, null, 2)}
            </pre>
          )}
        </div>
      )}
    </SectionContainer>
  )
}

const GdprSection = inject(
  USER_PROFILE_STORE,
  GENERAL_STORE
)(observer(GdprSectionComponent))

export { GdprSection }
